import { lastDanishBankDayOfMonth } from '@debbie/common/dist/economy/helpers/dates/danish-bank-days.js';
import helpers from '@debbie/common/dist/economy/helpers/index.js';
import * as paymentPlans from '@debbie/common/dist/economy/payment-plans/index.js';
import { Interval, } from '@debbie/common/dist/economy/payment-plans/index.js';
import { VoucherTypeSetting } from '@debbie/common/dist/economy/vouchers/index.js';
import { CaseVoucherInterestSettings } from '@debbie/common/dist/economy/vouchers/setup.js';
import { useCaseConditions } from '@debbie/cortex/dist/case-conditions/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useLimitationDocuments } from '@debbie/cortex/dist/documents/index.js';
import { useFixedDistributionsByCaseId } from '@debbie/cortex/dist/fixed-distributions/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../../theme.js';
import ArrowRight from '../../Icons/ArrowRight.js';
import Money from '../../Money.js';
const PaymentPlanSuggestions = (props) => {
    const { standardPlans, singleCase, caseVouchers, selectedPlan, onSelectPlan, } = props;
    const lang = useSelector((s) => s.lang);
    const hasInterest = props.caseVouchers.some((v) => v.setting !== null &&
        v.setting !== undefined &&
        CaseVoucherInterestSettings.includes(v.setting));
    const hasVariableInterest = props.caseVouchers.some((v) => v.setting === VoucherTypeSetting.InterestFromTable);
    return (React.createElement("div", null,
        standardPlans &&
            singleCase &&
            standardPlans.map((plan) => (React.createElement(Plan, { duration: plan.duration, interval: plan.interval, startDate: plan.interval === Interval.LASTBANKDAYMONTH
                    ? lastDanishBankDayOfMonth(moment().add(1, 'month'))
                    : moment()
                        .startOf('month')
                        .add(1, 'months')
                        .add(moment().format('HH:mm:ss')), active: selectedPlan === plan.id, singleCase: singleCase, caseVouchers: caseVouchers, onSelectPlan: onSelectPlan, id: plan.id, key: plan.id }))),
        (hasInterest || hasVariableInterest) && (React.createElement(Disclaimers, null,
            hasInterest && (React.createElement(DisclaimerLine, null, lang.paymentPlans.disclaimerLastEntryVaries)),
            hasVariableInterest && (React.createElement(DisclaimerLine, null, lang.paymentPlans.disclaimerVariableInterest))))));
};
export default PaymentPlanSuggestions;
// What unit to show next to the duration count (e.g. "6 måneder", "12 afdrag").
const durationUnitLabel = (interval, lang) => {
    switch (interval) {
        case Interval.MONTH:
        case Interval.LASTBANKDAYMONTH:
            return lang.paymentPlans.months;
        default:
            return lang.paymentPlans.entries;
    }
};
// Cadence description shown as a sub-label (e.g. "Månedligt", "Ugentligt").
const cadenceLabel = (interval, lang) => {
    switch (interval) {
        case Interval.LASTBANKDAYMONTH:
            return lang.paymentPlans.monthly;
        case Interval.QUARTERLY:
            return lang.paymentPlans.quarterly;
        case Interval.YEARLY:
            return lang.paymentPlans.yearly;
        case Interval['TWO-MONTHS']:
            return lang.paymentPlans.everyOtherMonth;
        case Interval.MONTH:
            return lang.paymentPlans.monthly;
        case Interval['TWO-WEEKS']:
            return lang.paymentPlans.everyOtherWeek;
        case Interval.WEEK:
            return lang.paymentPlans.weekly;
        default:
            return interval;
    }
};
// Period for the price subtitle (e.g. "DKK 1250 / måned", "/ afdrag").
const perEntryLabel = (interval, lang) => {
    switch (interval) {
        case Interval.MONTH:
        case Interval.LASTBANKDAYMONTH:
            return lang.paymentPlans.month;
        default:
            return lang.paymentPlans.entry;
    }
};
const Plan = (props) => {
    const [plan, setPlan] = useState(null);
    const lang = useSelector((s) => s.lang);
    const tenant = useTenant();
    const documentsLoader = useLimitationDocuments(props.singleCase.caseId);
    const documents = h.hasData(documentsLoader) ? documentsLoader.data : null;
    const creditorLoadable = useCreditor(props.singleCase.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const fixedDistributionsLoader = useFixedDistributionsByCaseId(props.singleCase.caseId);
    const fixedDistributions = h.hasData(fixedDistributionsLoader)
        ? fixedDistributionsLoader.data
        : null;
    const conditionsLoader = useCaseConditions(props.singleCase.caseId);
    const conditions = h.hasData(conditionsLoader) ? conditionsLoader.data : null;
    useEffect(() => {
        if (documents && tenant && creditor && fixedDistributions && conditions) {
            const filteredVouchers = helpers.vouchers.postProcessing(props.caseVouchers, {
                includeIrrecoverableVouchers: false,
                includeDeletedVouchers: false,
                includeExpiredVouchers: false,
            });
            const run = async () => {
                const feeStructure = helpers.paymentPlans.getEffectiveFeeStructure(creditor.paymentPlansConfig, tenant.paymentPlansConfig);
                const data = await helpers.paymentPlans.gen(tenant, creditor, props.singleCase.economyEngine, JSON.parse(JSON.stringify(filteredVouchers)), documents, fixedDistributions, {
                    startDate: props.startDate.toISOString(),
                    interval: props.interval || Interval.MONTH,
                    duration: props.duration,
                    feeStructure,
                    method: 'duration',
                }, conditions, { projectInterest: true });
                setPlan(data);
            };
            run();
        }
    }, [
        documents,
        tenant,
        creditor,
        fixedDistributions,
        conditions,
        props.singleCase.economyEngine,
        props.caseVouchers,
        props.startDate,
        props.interval,
        props.duration,
    ]);
    if (!plan) {
        return null;
    }
    const currency = props.singleCase?.currency;
    const upfront = paymentPlans.getAmount.upfront(plan);
    const rest = paymentPlans.getAmount.rest(plan);
    const monthlyAmount = paymentPlans.getAmount.entry(plan) || 0;
    const total = paymentPlans.getAmount.total(plan);
    const handleClick = () => props.active
        ? props.onSelectPlan(null, [])
        : props.onSelectPlan(props.id, plan);
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    };
    return (React.createElement(PlanCard, { $active: props.active, onClick: (e) => {
            handleClick();
        }, onKeyDown: handleKeyDown, role: "button", tabIndex: 0, "aria-pressed": props.active },
        props.active && React.createElement(ActiveCheck, { "aria-hidden": true }, "\u2713"),
        React.createElement(CardHeader, null,
            React.createElement(CardHeaderInfo, null,
                React.createElement(DurationPill, { $active: props.active },
                    props.duration,
                    ' ',
                    React.createElement(DurationUnit, null, durationUnitLabel(props.interval, lang))),
                React.createElement(CadenceLabel, null, cadenceLabel(props.interval, lang))),
            React.createElement(DateRange, null,
                React.createElement("span", null, moment(plan[0]?.date).format(THEME.DATE_FORMAT_SHORT)),
                React.createElement(ArrowRight, null),
                React.createElement("span", null, moment(plan[plan.length - 1]?.date).format(THEME.DATE_FORMAT_SHORT)))),
        React.createElement(PriceBlock, null,
            React.createElement(PriceMain, null,
                React.createElement(Money, { amount: monthlyAmount, currency: currency, noCalculator: true })),
            React.createElement(PriceSubtitle, null,
                "/",
                perEntryLabel(props.interval, lang))),
        React.createElement(Breakdown, null,
            !!upfront && (React.createElement(BreakdownRow, null,
                React.createElement(BreakdownLabel, null, lang.paymentPlans.upfrontPayment),
                React.createElement(BreakdownValue, null,
                    React.createElement(Money, { amount: upfront, currency: currency, noCalculator: true })))),
            !!rest && (React.createElement(BreakdownRow, null,
                React.createElement(BreakdownLabel, null, lang.paymentPlans.rest),
                React.createElement(BreakdownValue, null,
                    React.createElement(Money, { amount: rest, currency: currency, noCalculator: true })))),
            React.createElement(BreakdownRow, { $total: true },
                React.createElement(BreakdownLabel, null, lang.paymentPlans.totalPayment),
                React.createElement(BreakdownValue, { $total: true },
                    React.createElement(Money, { amount: total, currency: currency, noCalculator: true }))))));
};
const PlanCard = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 18px;
  border: 2px solid
    ${(props) => (props.$active ? THEME.COLOR_ACCENT : '#e5e5e5')};
  border-radius: 10px;
  background: ${(props) => props.$active ? `${THEME.COLOR_ACCENT}0d` : '#fff'};
  cursor: pointer;
  margin-bottom: 10px;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.05s ease;

  &:hover {
    border-color: ${(props) => (props.$active ? THEME.COLOR_ACCENT : '#bbb')};
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: scale(0.998);
  }

  &:focus-visible {
    outline: none;
    border-color: ${THEME.COLOR_ACCENT};
    box-shadow: 0 0 0 3px ${THEME.COLOR_ACCENT}33;
  }
`;
const ActiveCheck = styled.span `
  position: absolute;
  top: 10px;
  right: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${THEME.COLOR_ACCENT};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
`;
const CardHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding-right: 32px;
`;
const CardHeaderInfo = styled.div `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const CadenceLabel = styled.span `
  font-size: 12px;
  color: #666;
  font-weight: 500;
`;
const DurationPill = styled.div `
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${(props) => (props.$active ? THEME.COLOR_ACCENT : '#f0f0f0')};
  color: ${(props) => (props.$active ? '#fff' : '#222')};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
`;
const DurationUnit = styled.span `
  font-size: 12px;
  font-weight: 500;
  text-transform: lowercase;
  opacity: 0.85;
`;
const DateRange = styled.div `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  svg {
    width: 12px;
    height: 12px;
    opacity: 0.6;
  }
`;
const PriceBlock = styled.div `
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 4px;
  }
`;
const PriceMain = styled.span `
  font-size: clamp(2rem, 6vw, 2.6rem);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #111;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
`;
const PriceSubtitle = styled.span `
  font-size: 14px;
  color: #777;
  font-weight: 500;
`;
const Breakdown = styled.div `
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid #eee;
`;
const BreakdownRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  ${(props) => props.$total
    ? `
        margin-top: 4px;
        padding-top: 8px;
        border-top: 1px dashed #e5e5e5;
      `
    : ''}
`;
const BreakdownLabel = styled.span `
  font-size: 13px;
  color: #666;
`;
const BreakdownValue = styled.span `
  font-size: ${(props) => (props.$total ? '15px' : '13px')};
  font-weight: ${(props) => (props.$total ? 700 : 600)};
  color: ${(props) => (props.$total ? '#111' : '#333')};
  font-variant-numeric: tabular-nums;
`;
const Disclaimers = styled.div `
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 10px;
  margin-top: 2px;
  border-top: 1px solid #f0f0f0;
`;
const DisclaimerLine = styled.p `
  margin: 0;
  font-size: 11px;
  line-height: 1.4;
  color: #888;
  font-weight: 400;

  &::before {
    content: '*';
    margin-right: 4px;
    color: #b0b0b0;
  }
`;
//# sourceMappingURL=PaymentPlanSuggestions.js.map